/** @jsxImportSource @emotion/react */

import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import * as size from '@constant/size';
import * as color from '@constant/color';
import { IS_IYAGIGO_MOBILE_VIEW } from '@config/environment';
import TextBlock from '@component/common/block/text-block';
import IconBlock from '@component/common/block/icon-block';
import IconTextBlock from '@component/common/block/icon-text-block';
import TextButton from '@design-system/text-button';
import BottomContainer from '@component/common/architecture/bottom-container';
import Gap from '@component/common/gap';
import Blur from '@component/common/blur';
import { ETheme } from '@datatype/type';
import { useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import { useMySubscriptionQuery } from '@hook/billing/useApi';
import { EApiSubscriptionStatus } from '@datatype/billing/api-type';
import { actions as navigationActions } from '@store/slice/navigation-slice';
import { useNavigate } from 'react-router-dom';
import { IYAGIGO_PATH } from '@constant/iyagigo/route-path';
import { usePlaySound } from '@hook/common/useSound';
import { actions as workbookActions } from '@store/slice/workbook-slice';
import { actions as modalActions } from '@store/slice/modal-slice';
import {
  EApiAttendanceType,
  IClozeExplanation,
  IConversationData,
  IConversationGrammar,
  IConversationVocabulary,
  ISourceConversationData,
} from '@datatype/iyagigo/api-type';
import { IConversationProblemToastData, TClozeConversationPhase } from '@datatype/iyagigo/type';
import Scroll, { ScrollContainer } from '@component/common/scroll';
import ProblemControl from '@component/iyagigo/cloze/conversation-study/conversation-board/problem-control';
import ConversationBoard from '@component/iyagigo/cloze/conversation-study/conversation-board';
import ConversationKeyword from '@component/iyagigo/cloze/conversation-study/conversation-keyword';
import Explanation from '@component/iyagigo/common/explanation';
import ConversationKeyLine from '@component/iyagigo/cloze/conversation-study/conversation-key-line';
import ConversationNote from '@component/iyagigo/cloze/conversation-study/conversation-note';
import PlayingIndicator from '@component/iyagigo/cloze/conversation-study/conversation-board/playing-indicator';
import ImageAudioButton from '@design-system/image-audio-button';
import IconButton from '@design-system/icon-button';
import Spacer from '@component/common/spacer';
import ImageTextBlock from '@component/common/block/image-text-block';
import otterReadImage from '@asset/image/otter-read.png';

interface IProps {
  sourceKeyword: string;
  sourceKeywordPronunciation: string;
  meaning: string;
  sourceConversation: ISourceConversationData;
  conversation: IConversationData;
  conversationAudioLinks: string[] | null;
  sourceKeywordAudioLink: string | null;
  answerExplanation: IClozeExplanation | null;
  conversationVocabularies: IConversationVocabulary[] | null;
  conversationGrammars: IConversationGrammar[] | null;
  initialStep?: number;
  handleConversationStart?: () => void;
  handleConversationComplete?: () => void;
  handleProblemSubmit?: () => void;
  handleCompleteClick?: () => void;
  attendanceType?: EApiAttendanceType;
}

// ############### styled ###############
const FixContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
`;

const Wrapper = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  padding: ${size.PAGE_PADDING} ${size.PAGE_PADDING} 0 ${size.PAGE_PADDING};
  display: flex;
  flex-flow: column nowrap;
`;

const FixItemContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
`;

const SubscriptionLockContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  gap: ${size.MARGIN_1};
  padding: ${size.MARGIN_2};
  background-color: ${color.WHITE_1};
  border-radius: ${size.BORDER_RADIUS_1};
  box-shadow: ${color.SURFACE_SHADOW};
  pointer-events: auto;
`;

const LockRow = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: 5px;
`;

const BottomContentContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 100%;
  padding: 0px ${size.PAGE_PADDING} ${IS_IYAGIGO_MOBILE_VIEW ? '0px' : size.MARGIN_4} ${size.PAGE_PADDING};
`;

const NAVIGATION_SECONDARY_WIDTH = '80px';
const CONVERSATION_CONTROL_BUTTON_WIDTH = '55px';

const NavigationContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const WrapUpContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: center;
  width: 100%;
`;

const ConversationControlContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  gap: ${size.MARGIN_1};
  width: 100%;
`;

// ############### component ###############
const ConversationStudy: React.FC<IProps> = ({
  sourceKeyword,
  sourceKeywordPronunciation,
  meaning,
  sourceConversation,
  conversation,
  conversationAudioLinks,
  sourceKeywordAudioLink,
  answerExplanation,
  conversationVocabularies,
  conversationGrammars,
  initialStep,
  handleConversationStart,
  handleConversationComplete,
  handleProblemSubmit,
  handleCompleteClick,
  attendanceType,
}) => {
  const dispatch = useTypedDispatch();
  const playSound = usePlaySound();
  const navigate = useNavigate();
  const subscriptionQuery = useMySubscriptionQuery();
  const isSubscriber = subscriptionQuery.data?.data?.status === EApiSubscriptionStatus.ACTIVE;

  const handleSubscriptionWallClick = () => {
    dispatch(modalActions.hideOverlay());
    dispatch(navigationActions.storeIyagigoSubMenu({ mainMenu: 'hub', subMenu: 'subscription' }));
    navigate(IYAGIGO_PATH.HUB_SUBSCRIPTION_PATH);
  };

  const storeStep = useTypedSelector((state) => state.rootReducer?.workbookReducer?.clozeConversationStudyStep) ?? 1;
  // initialStep이 주어지면 첫 렌더부터 그 step 미만으로 내려가지 않도록 보정 (store dispatch 반영 전 깜빡임 방지)
  const step = initialStep != null ? Math.max(storeStep, initialStep) : storeStep;

  // initialStep이 주어지면 mount 시 store의 step도 동기화
  useEffect(() => {
    if (initialStep != null) {
      dispatch(workbookActions.storeClozeConversationStudyStep(initialStep));
    }
  }, []);

  // Step 3 진입 시 비구독자에게 구독 wall 자동 노출 (학습 노트 Premium 전용)
  useEffect(() => {
    if (step === 3 && !isSubscriber) {
      dispatch(
        modalActions.showWallToast({
          type: 'subscription-wall',
          data: { trigger: 'access' },
        }),
      );
    }
  }, [step, isSubscriber]);
  const lineCursor = useTypedSelector((state) => state.rootReducer?.workbookReducer?.clozeConversationLineCursor) ?? 0;
  const conversationPhase: TClozeConversationPhase = useTypedSelector(
    (state) => state.rootReducer?.workbookReducer?.clozeConversationPhase,
  ) ?? { type: 'conversation-playing' };

  const [selectedChoiceId, setSelectedChoiceId] = useState<string | null>(null);
  // 현재 라인이 사용자 클릭(시작하기/이어가기) 대기 중인지.
  // true: thinking 미리보기 + 버튼 노출, false: typing 진행 중 또는 완료된 라인.
  const [isPending, setIsPending] = useState<boolean>(true);

  const lines = sourceConversation.line;
  const problems = sourceConversation.problem;

  // 현재 phase에서의 문제 데이터
  const activeProblemIndex =
    conversationPhase.type === 'conversation-problem-choice' || conversationPhase.type === 'conversation-problem-check'
      ? conversationPhase.problemIndex
      : null;
  const currentSourceProblem = activeProblemIndex != null ? problems[activeProblemIndex] : null;
  const currentConvProblem = activeProblemIndex != null ? conversation.problem[activeProblemIndex] : null;

  // ############### handler ###############
  const handleChoiceSelect = (choiceId: string) => {
    // playSound('tap');
    setSelectedChoiceId(choiceId);
  };

  // REMARK: 미사용 Clip 학습의 quota 흐름을 보존한다. 한도 문구 확장 대상에서는 제외한다.
  const handleChoiceSubmit = () => {
    if (!selectedChoiceId || conversationPhase.type !== 'conversation-problem-choice' || !currentSourceProblem) return;
    const problemIdx = conversationPhase.problemIndex;
    // 비구독자는 3번째 problem(index 2)부터 구독 wall로 차단
    if (!isSubscriber && problemIdx >= 2) {
      dispatch(
        modalActions.showWallToast({
          type: 'subscription-wall',
          data: { trigger: 'quota', attendanceType },
        }),
      );
      return;
    }
    handleProblemSubmit?.();
    const selectedIdx = Number(selectedChoiceId);
    const isCorrect = currentSourceProblem.choice[selectedIdx]?.correct ?? false;
    playSound(isCorrect ? 'correct' : 'incorrect');

    const choices = currentSourceProblem.choice.map((sourceChoice, idx) => {
      const convChoice = currentConvProblem?.choice[idx];
      return {
        sentence: sourceChoice.sentence,
        pronunciation: sourceChoice.pronunciation,
        translation: convChoice?.translation ?? '',
        explanation: convChoice?.explanation ?? '',
        isCorrect: sourceChoice.correct,
        isSelected: idx === selectedIdx,
      };
    });

    const toastData: IConversationProblemToastData = {
      type: 'conversation-problem',
      data: {
        isCorrect,
        choices,
        problemExplanation: currentConvProblem?.explanation ?? '',
      },
    };
    dispatch(modalActions.showNotificationToast(toastData));
    dispatch(
      workbookActions.storeClozeConversationPhase({
        type: 'conversation-problem-check',
        problemIndex: problemIdx,
      }),
    );
  };

  // toast 닫힘 감지 → result phase일 때 대화 이어가기
  const isNotificationToast =
    useTypedSelector((state) => state.rootReducer?.modalReducer?.isNotificationToast) ?? false;

  useEffect(() => {
    if (!isNotificationToast && conversationPhase.type === 'conversation-problem-check') {
      const problemLineIndex = problems[conversationPhase.problemIndex]?.lineIndex ?? 0;
      // 정답 라인은 typing 없이 즉시 plain으로 표시 → lineCursor를 다음 라인으로 진행
      dispatch(workbookActions.storeClozeConversationLineCursor(problemLineIndex + 1));
      setSelectedChoiceId(null);
      setIsPending(true);
      dispatch(workbookActions.storeClozeConversationPhase({ type: 'conversation-playing' }));
    }
  }, [isNotificationToast]);

  // "시작하기/이어가기" 클릭: 대기 해제 → 현재 라인의 typing 시작.
  const handleContinueClick = () => {
    // playSound('tap');
    // 비구독자는 3번째 라인(lineCursor 2)부터 구독 wall로 차단
    if (!isSubscriber && lineCursor >= 2) {
      dispatch(
        modalActions.showWallToast({
          type: 'subscription-wall',
          data: { trigger: 'quota', attendanceType },
        }),
      );
      return;
    }
    if (lineCursor === 0) {
      handleConversationStart?.();
    }
    setIsPending(false);
  };

  // typing 완료 콜백: lineCursor 증가, 다음 라인을 다시 사용자 클릭 대기 상태로.
  const handleTypingCompleted = useCallback(() => {
    setIsPending(true);
    dispatch(workbookActions.incrementClozeConversationLineCursor());
  }, [dispatch]);

  // 마지막 라인까지 재생 완료 시 자동으로 done phase 전환
  useEffect(() => {
    if (conversationPhase.type === 'conversation-playing' && lines.length > 0 && lineCursor >= lines.length) {
      dispatch(workbookActions.storeClozeConversationPhase({ type: 'conversation-complete' }));
    }
  }, [conversationPhase.type, lineCursor, lines.length]);

  // step별 다음/이전 동작 통합
  const handleNextButtonClick = () => {
    // playSound('tap');
    if (step === 1) {
      dispatch(workbookActions.storeClozeConversationStudyStep(2));
      dispatch(workbookActions.resetClozeConversation());
    } else if (step === 2) {
      handleConversationComplete?.();
      dispatch(workbookActions.storeClozeConversationStudyStep(3));
    } else if (step === 3) {
      // 대화학습 종료 → 부모 cloze(빈칸문제)로 복귀
      dispatch(workbookActions.storeIsClozeConversationStudy(false));
      dispatch(workbookActions.resetClozeConversation());
      handleCompleteClick?.();
    }
  };

  const minStep = initialStep ?? 1;
  const handlePreviousButtonClick = () => {
    // playSound('tap');
    if (step === 2 && minStep < 2) {
      dispatch(workbookActions.storeClozeConversationStudyStep(1));
    } else if (step === 3 && minStep < 3) {
      dispatch(workbookActions.storeClozeConversationStudyStep(2));
      dispatch(workbookActions.storeClozeConversationPhase({ type: 'conversation-complete' }));
    }
  };

  // ############### render steps ###############

  const keyLineIndex = sourceConversation.keyLine?.index ?? 0;
  const keySourceLine = lines[keyLineIndex];
  const keyConvLine = conversation.line[keyLineIndex];
  const keyAudioLink = conversationAudioLinks?.[keyLineIndex] ?? null;

  return (
    <Wrapper>
      <FixContainer>
        {step === 1 && (
          <ConversationKeyword
            keyword={sourceKeyword}
            pronunciation={sourceKeywordPronunciation}
            meaning={meaning}
            keywordAudioLink={sourceKeywordAudioLink}
          />
        )}
        {step === 2 && (
          <FixItemContainer>
            <TextBlock
              text={conversation.situation}
              design={{ text: { size: 'medium', color: color.BLACK_2, bold: true }, box: { alignX: 'left' } }}
            />
          </FixItemContainer>
        )}
        {step === 3 && (
          <FixItemContainer>
            <Blur
              blur={!isSubscriber}
              width='100%'
              icon={
                <SubscriptionLockContainer>
                  <LockRow>
                    <IconBlock icon='lock' design={{ icon: { size: 'small', color: color.GRAY_1 } }} />
                    <TextBlock
                      text={'Plus⁺ 회원만 볼 수 있어요'}
                      design={{
                        text: { size: 'medium', color: color.GRAY_1, bold: false },
                        box: { alignX: 'center' },
                      }}
                    />
                  </LockRow>
                  <TextBlock
                    text={'Plus⁺ 시작하기'}
                    design={{
                      text: {
                        size: 'medium',
                        color: color.BROWN_1,
                        bold: false,
                        underline: true,
                      },
                      box: { alignX: 'center', event: { cursor: 'pointer' } },
                    }}
                    click={{ handler: handleSubscriptionWallClick }}
                  />
                </SubscriptionLockContainer>
              }>
              <ConversationKeyLine
                sentence={keySourceLine?.sentence ?? ''}
                pronunciation={keySourceLine?.pronunciation}
                translation={keyConvLine?.translation ?? ''}
                explanation={keyConvLine?.explanation ?? ''}
                audioLink={keyAudioLink}
              />
              <Gap height={size.MARGIN_2} />
              <IconTextBlock
                icon='book'
                text={'대화에서 짚어볼 어휘·문법'}
                arrangement='icon-text'
                design={{
                  icon: { kind: 'outlined', size: 'small', color: color.BLACK_2 },
                  text: { size: 'medium', color: color.BLACK_2, bold: true, underline: true },
                  box: { alignX: 'left', gap: '6px' },
                }}
              />
            </Blur>
          </FixItemContainer>
        )}
      </FixContainer>
      <Gap height={step === 1 ? size.MARGIN_1 : step === 2 ? size.MARGIN_1 : step === 3 ? size.MARGIN_1 : '0px'} />
      <ScrollContainer style={{ backgroundColor: step === 2 ? color.BROWN_5 : undefined }}>
        {/* step 전환 시 Scroll 위치 reset 위해 key 강제 변경 */}
        <Scroll key={step} direction='y' height='100%'>
          <ContentContainer>
            {step === 1 && (
              <Explanation
                answerExplanation={answerExplanation}
                clozeType='vocabulary'
                labelSize='medium'
                sections={['answer', 'usage', 'example', 'kanji', 'alternative']}
              />
            )}
            {step === 2 && (
              <ConversationBoard
                sourceConversation={sourceConversation}
                conversation={conversation}
                conversationAudioLinks={conversationAudioLinks}
                keyword={sourceKeyword}
                isPending={isPending}
                onTypingCompleted={handleTypingCompleted}
              />
            )}
            {step === 3 && (
              <Blur blur={!isSubscriber} width='100%'>
                <ConversationNote vocabularies={conversationVocabularies} grammars={conversationGrammars} />
              </Blur>
            )}
          </ContentContainer>
        </Scroll>
      </ScrollContainer>
      <Gap height={step === 1 ? size.MARGIN_1 : step === 2 ? size.MARGIN_1 : step === 3 ? size.MARGIN_1 : '0px'} />
      <BottomContainer
        position='relative'
        height={step === 2 ? (IS_IYAGIGO_MOBILE_VIEW ? '200px' : '300px') : 'auto'}
        backgroundColor={color.WHITE_1}
        margin={`0 -${size.PAGE_PADDING}`}
        animationKey={
          step === 1
            ? undefined
            : step === 2
              ? conversationPhase.type === 'conversation-problem-choice'
                ? `problem-${conversationPhase.problemIndex}`
                : conversationPhase.type === 'conversation-playing' && !isPending
                  ? `progress-${lineCursor}`
                  : undefined
              : step === 3
                ? undefined
                : undefined
        }>
        <BottomContentContainer>
          {step === 1 && (
            <>
              <Spacer />
              <TextButton
                theme={ETheme.BROWN}
                size='medium'
                shape='pill'
                width={'100%'}
                text={'이야기 보기'}
                handleClick={handleNextButtonClick}
              />
            </>
          )}
          {step === 2 && (
            <>
              {conversationPhase.type === 'conversation-playing' && lineCursor < lines.length && isPending && (
                <>
                  <PlayingIndicator current={lineCursor + 1} total={lines.length} />
                  <Spacer />
                  <TextButton
                    theme={ETheme.BROWN}
                    size='medium'
                    shape='pill'
                    width={'100%'}
                    text={lineCursor === 0 ? '시작하기' : '이어듣기'}
                    handleClick={handleContinueClick}
                  />
                </>
              )}
              {conversationPhase.type === 'conversation-playing' && lineCursor < lines.length && !isPending && (
                <PlayingIndicator current={lineCursor + 1} total={lines.length} />
              )}
              {conversationPhase.type === 'conversation-problem-choice' && currentSourceProblem && (
                <ProblemControl
                  sourceProblem={currentSourceProblem}
                  selectedChoiceId={selectedChoiceId}
                  onChoiceSelect={handleChoiceSelect}
                  onChoiceSubmit={handleChoiceSubmit}
                />
              )}
              {conversationPhase.type === 'conversation-complete' && (
                <>
                  <ConversationControlContainer>
                    {conversationAudioLinks && conversationAudioLinks.length > 0 && (
                      <ImageAudioButton
                        theme={ETheme.BROWN}
                        kind='outlined'
                        size='medium'
                        shape='square'
                        width={CONVERSATION_CONTROL_BUTTON_WIDTH}
                        sources={conversationAudioLinks}
                        timing={{ gap: 300 }}
                        // timing={{ gap: 500 }}
                      />
                    )}
                    <IconButton
                      icon='replay'
                      theme={ETheme.BROWN}
                      kind='outlined'
                      size='medium'
                      shape='square'
                      width={CONVERSATION_CONTROL_BUTTON_WIDTH}
                      handleClick={() => {
                        dispatch(workbookActions.storeClozeConversationLineCursor(0));
                        dispatch(workbookActions.storeClozeConversationPhase({ type: 'conversation-playing' }));
                        setIsPending(true);
                      }}
                    />
                  </ConversationControlContainer>
                  <WrapUpContainer>
                    <ImageTextBlock
                      src={otterReadImage}
                      alt='otter'
                      text={'이야기 한 편 완료!\n표현을 자세히 살펴봐요'}
                      design={{
                        image: { height: '60px' },
                        text: { size: 'large', color: color.BLACK_2, bold: true },
                        box: { gap: size.MARGIN_1, alignX: 'center', alignY: 'bottom' },
                      }}
                    />
                  </WrapUpContainer>
                  <NavigationContainer>
                    {step > minStep && (
                      <TextButton
                        theme={ETheme.BROWN}
                        kind='outlined'
                        size='medium'
                        shape='square'
                        width={NAVIGATION_SECONDARY_WIDTH}
                        text={'이전으로'}
                        handleClick={handlePreviousButtonClick}
                      />
                    )}
                    <TextButton
                      theme={ETheme.BROWN}
                      size='medium'
                      shape='pill'
                      width={step > minStep ? `calc(100% - ${NAVIGATION_SECONDARY_WIDTH} - ${size.MARGIN_1})` : '100%'}
                      text={'다음으로'}
                      handleClick={handleNextButtonClick}
                    />
                  </NavigationContainer>
                </>
              )}
            </>
          )}
          {step === 3 && (
            <>
              <Spacer />
              <NavigationContainer>
                {step > minStep && (
                  <TextButton
                    theme={ETheme.BROWN}
                    kind='outlined'
                    size='medium'
                    shape='square'
                    width={NAVIGATION_SECONDARY_WIDTH}
                    text={'이전으로'}
                    handleClick={handlePreviousButtonClick}
                  />
                )}
                <TextButton
                  theme={ETheme.BROWN}
                  size='medium'
                  shape='pill'
                  width={step > minStep ? `calc(100% - ${NAVIGATION_SECONDARY_WIDTH} - ${size.MARGIN_1})` : '100%'}
                  text={'이야기 학습 끝내기'}
                  handleClick={handleNextButtonClick}
                />
              </NavigationContainer>
            </>
          )}
        </BottomContentContainer>
      </BottomContainer>
    </Wrapper>
  );
};

export default ConversationStudy;
